import * as rxjs from 'rxjs'

/**
 * Combines the latest values from the observables or switches to the
 * observable from the producer function, will ever emit only once.
 *
 * Additionally it can filter the output based on a predicate function, and
 * after the predicate passes, it will emit the value and complete the
 * observable.
 *
 * @example
 * // example 1:
 * rxjs
 *   .of(1, 2, 3)
 *   .pipe(
 *     latestFrom(
 *       (input: number) => rxjs.of(input * 2),
 *       (result: number) => result > 4,
 *     ),
 *   )
 *   .subscribe(result => console.log(result)) // Output: 6
 *
 * // example 2:
 * rxjs
 *   .of(srcValue1)
 *   .pipe(
 *     latestFrom(
 *       src => ({
 *         srcValue1: rxjs.of(src),
 *         srcValue2: srcValue2$,
 *       }),
 *       result => result.srcValue1 !== undefined,
 *     ),
 *   )
 *   .subscribe(result => console.log(result)) // Output: { srcValue1: 1, srcValue2: 1}
 *
 * TODO: Split this into two methods: latestFrom and latestFromWhere.
 * The current implementation occasionally causes the predicate to behave unpredictably
 */
export function latestFrom<
  TInput,
  const TOutput extends
    | Object.Constraint
    | readonly unknown[]
    | rxjs.Observable<any>,
  TFilteredOutput extends UnpackObservables<TOutput>,
>(
  producer: (input: TInput) => TOutput,
  predicate?: (result: UnpackObservables<TOutput>) => result is TFilteredOutput,
): rxjs.OperatorFunction<TInput, TFilteredOutput>
export function latestFrom<
  TInput,
  const TOutput extends
    | Object.Constraint
    | readonly unknown[]
    | rxjs.Observable<any>,
>(
  producer: (input: TInput) => TOutput,
  predicate?: (result: UnpackObservables<TOutput>) => boolean,
): rxjs.OperatorFunction<TInput, UnpackObservables<TOutput>>
export function latestFrom<
  TInput,
  const TOutput extends
    | Object.Constraint
    | readonly unknown[]
    | rxjs.Observable<any>,
>(
  producer: (input: TInput) => TOutput,
  predicate?: (result: UnpackObservables<TOutput>) => boolean,
): rxjs.OperatorFunction<TInput, UnpackObservables<TOutput>> {
  return source$ =>
    source$.pipe(
      rxjs.switchMap(input => {
        const output = producer(input)
        let output$: rxjs.Observable<UnpackObservables<TOutput>>

        // If the output is an observable, we can simply switch the context
        // (rxjs.switchMap) to it. However, if the output is an object or a
        // tuple, we need to combine all the observables inside it.
        if (output instanceof rxjs.Observable) {
          // NOTE:  Due to how typescript handles deferred evaluation, we need to cast
          // the output to the correct type
          output$ = output as typeof output$
        } else {
          output$ = rxjs.combineLatest(
            // NOTE:  Due to how 'rxjs.combineLatest' is setup, TypeScript requires us to
            // cast the output to the object type in order to correctly infer the type.
            // This is necessary because 'rxjs.combineLatest' accepts both objects and tuples,
            // but TypeScript cannot infer the correct type without the explicit cast.
            // Technically tuple already satisfies Object.Constraint.
            output satisfies Object.Constraint as Object.Constraint,
            // NOTE:  Due to how typescript handles deferred evaluation, we need to cast
            // the output to the correct type
          ) as typeof output$
        }

        // If a predicate is provided, we filter the output based on the
        // predicate.
        if (predicate) {
          output$ = output$.pipe(rxjs.filter(predicate))
        }

        // We only ever want a one emission, so we complete the observable
        // NOTE: If there is a need for additional emissions in the future,
        // consider using rxjs.take(configParam)
        // NOTE: The `take` operator needs to be piped here, and not on the
        // source observable, so it doesn't accidentally complete the source
        // observable. Placing it here ensures that only the first emission
        // from the `output$` observable is returned.
        // NOTE: Avoid using `first()` here because it can cause unexpected behavior.
        // If the source observable emits only one value and the predicate filters it out,
        // `first()` will throw an error. Using `take(1)` ensures that we only take the first
        // emission without throwing an error if the predicate doesn't pass.
        return output$.pipe(rxjs.take(1))
      }),
    )
}

/**
 * Unpacks the input type `T` to its corresponding value type.
 * If `T` is an observable, it unpacks it to the value type of the observable.
 * If `T` is an object, it unpacks each property to its value type.
 * If `T` is a tuple, it unpacks each element to its value type.
 */
export type UnpackObservables<T> =
  T extends rxjs.Observable<infer _T>
    ? _T
    : {
        [K in keyof T]: rxjs.ObservedValueOf<NonNullable<T[K]>>
      }
